/** Phase 9 Slice 2 — /app/campaign-templates — tenant custom template authoring.
 *
 * List (built-in + tenant-custom rows) + create-from-blank + duplicate-built-in
 * + edit (form modal) + delete (custom only). Built-in rows render with a
 * lock chip and the destructive actions are disabled.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useLocale } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle }
  from "../../components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Copy, Edit2, Lock, Plus, Trash2 } from "lucide-react";

const COPY = {
  de: {
    title: "Kampagnen-Vorlagen",
    subtitle: "Built-in Archetypen + eigene Vorlagen für deinen Tenant.",
    new: "Neue Vorlage", duplicate: "Duplizieren",
    edit: "Bearbeiten", del: "Löschen",
    built_in: "Built-in", custom: "Eigene",
    confirm_del: "Vorlage wirklich löschen?",
    form: {
      archetype_key: "Archetype-Key (eindeutig)",
      industry: "Branche", name_de: "Anzeigename (DE)",
      name_en: "Anzeigename (EN)", desc_de: "Beschreibung (DE)",
      kind: "Kampagnen-Typ", budget: "Budget €/Tag",
      runtime: "Laufzeit (Tage)", regions: "Regionen (Komma-getrennt)",
      platforms: "Plattformen (Komma-getrennt)",
      brief_de: "Briefing (DE)", brief_en: "Briefing (EN)",
      compliance_de: "Compliance-Hinweise (DE)",
      compliance_en: "Compliance-Hinweise (EN)",
      cancel: "Abbrechen", save: "Speichern",
    },
    saved: "Gespeichert.",
    err: "Fehler.",
  },
  en: {
    title: "Campaign Templates",
    subtitle: "Built-in archetypes + custom templates for your tenant.",
    new: "New template", duplicate: "Duplicate",
    edit: "Edit", del: "Delete",
    built_in: "Built-in", custom: "Custom",
    confirm_del: "Delete this template?",
    form: {
      archetype_key: "Archetype key (unique)",
      industry: "Industry", name_de: "Display name (DE)",
      name_en: "Display name (EN)", desc_de: "Description (DE)",
      kind: "Campaign kind", budget: "Budget €/day",
      runtime: "Runtime (days)", regions: "Regions (comma-separated)",
      platforms: "Platforms (comma-separated)",
      brief_de: "Brief (DE)", brief_en: "Brief (EN)",
      compliance_de: "Compliance notes (DE)",
      compliance_en: "Compliance notes (EN)",
      cancel: "Cancel", save: "Save",
    },
    saved: "Saved.",
    err: "Error.",
  },
};
const tt = (l, k) => {
  const parts = k.split(".");
  let cur = COPY[l] || COPY.de;
  for (const p of parts) cur = cur && cur[p];
  return typeof cur === "string" ? cur : k;
};
const loc = (o, l) => (o && (o[l] || o.de || o.en)) || "";

function csvToList(s) {
  return (s || "").split(",").map(x => x.trim()).filter(Boolean);
}

export default function CampaignTemplatesAdmin() {
  const { locale } = useLocale();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);  // null | row | "new"

  async function load() {
    try {
      const r = await api.get("/campaign-templates");
      setRows(r.data.templates || []);
    } catch (_) { /* surfaced via toasts on actions */ }
  }
  useEffect(() => { load(); }, []);

  async function duplicate(id) {
    try {
      await api.post(`/campaign-templates/${id}/duplicate`);
      toast.success(tt(locale, "saved"));
      await load();
    } catch (_) { toast.error(tt(locale, "err")); }
  }
  async function remove(id) {
    if (!window.confirm(tt(locale, "confirm_del"))) return;
    try {
      await api.delete(`/campaign-templates/${id}`);
      toast.success(tt(locale, "saved"));
      await load();
    } catch (_) { toast.error(tt(locale, "err")); }
  }

  const builtIns = rows.filter(r => r.source === "built_in");
  const customs = rows.filter(r => r.source === "tenant_custom");

  return (
    <div className="p-6 space-y-6" data-testid="campaign-templates-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {tt(locale, "title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {tt(locale, "subtitle")}
          </p>
        </div>
        <Button data-testid="tpl-admin-new" onClick={() => setEditing("new")}>
          <Plus className="size-4 mr-1" />
          {tt(locale, "new")}
        </Button>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground">
          {tt(locale, "built_in")} ({builtIns.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {builtIns.map(r => (
            <TemplateCard
              key={r.id} row={r} locale={locale} readOnly
              onDuplicate={() => duplicate(r.id)} />
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground">
          {tt(locale, "custom")} ({customs.length})
        </h2>
        {customs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic"
              data-testid="tpl-admin-custom-empty">—</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {customs.map(r => (
              <TemplateCard
                key={r.id} row={r} locale={locale} readOnly={false}
                onEdit={() => setEditing(r)}
                onDelete={() => remove(r.id)}
                onDuplicate={() => duplicate(r.id)} />
            ))}
          </div>
        )}
      </section>

      {editing && (
        <EditDialog
          locale={locale}
          row={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function TemplateCard({ row, locale, readOnly, onEdit, onDelete, onDuplicate }) {
  return (
    <Card data-testid={`tpl-admin-card-${row.archetype_key}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm">
            {loc(row.display_name, locale)}
          </CardTitle>
          {readOnly && (
            <Badge variant="outline" className="font-mono text-[10px]"
                    data-testid={`tpl-admin-locked-${row.archetype_key}`}>
              <Lock className="size-3 mr-1" /> built-in
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        <p className="text-muted-foreground line-clamp-2">
          {loc(row.description, locale) || "—"}
        </p>
        <div className="flex flex-wrap gap-1">
          <Badge variant="secondary" className="font-mono text-[10px]">
            {row.default_campaign_kind}
          </Badge>
          <Badge variant="outline" className="font-mono text-[10px]">
            €{row.default_budget_eur_per_day}/Tag
          </Badge>
          <Badge variant="outline" className="font-mono text-[10px]">
            {row.default_runtime_days}d
          </Badge>
        </div>
        <div className="flex justify-end gap-1 pt-2">
          <Button size="sm" variant="ghost" onClick={onDuplicate}
                  data-testid={`tpl-admin-dup-${row.archetype_key}`}>
            <Copy className="size-3 mr-1" />
          </Button>
          <Button size="sm" variant="ghost"
                  disabled={readOnly}
                  onClick={onEdit}
                  data-testid={`tpl-admin-edit-${row.archetype_key}`}>
            <Edit2 className="size-3 mr-1" />
          </Button>
          <Button size="sm" variant="ghost"
                  disabled={readOnly}
                  onClick={onDelete}
                  data-testid={`tpl-admin-del-${row.archetype_key}`}>
            <Trash2 className="size-3 mr-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditDialog({ row, locale, onClose, onSaved }) {
  const isNew = !row;
  const [form, setForm] = useState(() => ({
    archetype_key: row?.archetype_key || "",
    industry: row?.industry || "real_estate",
    display_name_de: row?.display_name?.de || "",
    display_name_en: row?.display_name?.en || "",
    description_de: row?.description?.de || "",
    default_campaign_kind: row?.default_campaign_kind || "lead",
    default_budget_eur_per_day: row?.default_budget_eur_per_day ?? 50,
    default_runtime_days: row?.default_runtime_days ?? 30,
    default_regions_csv: (row?.default_regions || []).join(", "),
    default_platforms_csv: (row?.default_platforms || []).join(", "),
    brief_de: row?.brief_template?.de || "",
    brief_en: row?.brief_template?.en || "",
    compliance_de: row?.compliance_notes?.de || "",
    compliance_en: row?.compliance_notes?.en || "",
  }));
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    const body = {
      archetype_key: form.archetype_key,
      industry: form.industry,
      display_name: { de: form.display_name_de, en: form.display_name_en },
      description: { de: form.description_de, en: "" },
      default_campaign_kind: form.default_campaign_kind,
      default_platforms: csvToList(form.default_platforms_csv),
      default_regions: csvToList(form.default_regions_csv),
      default_budget_eur_per_day: Number(form.default_budget_eur_per_day) || 0,
      default_runtime_days: Number(form.default_runtime_days) || 30,
      brief_template: { de: form.brief_de, en: form.brief_en },
      compliance_notes: { de: form.compliance_de, en: form.compliance_en },
    };
    try {
      if (isNew) await api.post("/campaign-templates", body);
      else await api.patch(`/campaign-templates/${row.id}`, body);
      toast.success(tt(locale, "saved"));
      await onSaved();
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(code || tt(locale, "err"));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="tpl-admin-form"
        className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? tt(locale, "new") : tt(locale, "edit")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={tt(locale, "form.archetype_key")}>
            <Input data-testid="tpl-admin-archetype-key"
                    value={form.archetype_key}
                    onChange={(e) => set("archetype_key", e.target.value)}
                    disabled={!isNew}/>
          </Field>
          <Field label={tt(locale, "form.industry")}>
            <Input value={form.industry}
                    onChange={(e) => set("industry", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.name_de")}>
            <Input data-testid="tpl-admin-name-de"
                    value={form.display_name_de}
                    onChange={(e) => set("display_name_de", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.name_en")}>
            <Input value={form.display_name_en}
                    onChange={(e) => set("display_name_en", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.kind")}>
            <Input value={form.default_campaign_kind}
                    onChange={(e) => set("default_campaign_kind", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.budget")}>
            <Input type="number" min={0}
                    value={form.default_budget_eur_per_day}
                    onChange={(e) => set("default_budget_eur_per_day", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.runtime")}>
            <Input type="number" min={1}
                    value={form.default_runtime_days}
                    onChange={(e) => set("default_runtime_days", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.regions")}>
            <Input value={form.default_regions_csv}
                    onChange={(e) => set("default_regions_csv", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.platforms")}>
            <Input value={form.default_platforms_csv}
                    onChange={(e) => set("default_platforms_csv", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.desc_de")} full>
            <textarea
              className="w-full h-20 text-xs font-mono p-2 rounded-md border bg-background"
              value={form.description_de}
              onChange={(e) => set("description_de", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.brief_de")} full>
            <textarea
              className="w-full h-32 text-xs font-mono p-2 rounded-md border bg-background"
              value={form.brief_de}
              onChange={(e) => set("brief_de", e.target.value)}/>
          </Field>
          <Field label={tt(locale, "form.compliance_de")} full>
            <textarea
              className="w-full h-20 text-xs font-mono p-2 rounded-md border bg-background"
              value={form.compliance_de}
              onChange={(e) => set("compliance_de", e.target.value)}/>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {tt(locale, "form.cancel")}
          </Button>
          <Button onClick={save} disabled={saving}
                  data-testid="tpl-admin-save">
            {tt(locale, "form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={"space-y-1" + (full ? " md:col-span-2" : "")}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
